/** Phase-2 content: new places (sports centre, mall, stables, nurse, music room) and their people. */
export const enPlaces = {
  places: {
    zones: {
      gym: 'Gym', pool: 'Swimming pool', nurse: 'Nurse’s office', music: 'Music room', cafe: 'Little Lantern Café',
      giftShop: 'Gift shop', dept: 'Department store', restaurant: 'Restaurant', mall: 'Lantern Mall',
      sportsCenter: 'Sports centre', tennis: 'Tennis court', volley: 'Beach volleyball', stable: 'Riding stables',
    },
    mapNames: { sports: 'Sports centre', mall: 'Lantern Mall' },
    objects: {
      nurseDesk: 'Nurse’s desk', pool: 'Pool', gymNet: 'Volleyball net', tennisNet: 'Tennis court', volleyNet: 'Volleyball court',
      fountain: 'Fountain', cafeCounter: 'Café counter', giftCounter: 'Gift counter', deptCounter: 'Checkout', restCounter: 'Restaurant counter',
    },
    npc: {
      nurse: 'Nurse Hsieh', musicTeacher: 'Mr. Kuo', lifeguard: 'Lifeguard Ann', barista: 'Mei', giftClerk: 'Lulu',
      deptClerk: 'Mr. Pan', waiter: 'Tom', stableKeeper: 'Uncle Wu', horse: 'Horse', pony: 'Pony',
    },
    items: {
      flowers: { name: 'Bouquet', desc: 'A gift that makes anyone smile.' },
      scarf: { name: 'Scarf', desc: 'A soft, warm gift.' },
      keychain: { name: 'Cute keychain', desc: 'A small gift for a friend.' },
      toy: { name: 'Toy car', desc: 'Kids love it.' },
      sandwich: { name: 'Sandwich', desc: 'Eat: +14 energy' },
      cocoa: { name: 'Cocoa to go', desc: 'Drink: +12 energy, +4 mood' },
    },
    shops: {
      cafe: { title: '☕ Little Lantern Café', hint: 'Warm drinks and snacks.' },
      gift: { title: '🎁 Gift shop', hint: 'Gifts make people happy. Give them when you chat!' },
      dept: { title: '👕 Department store', hint: 'New clothes change your look right away.' },
    },
    outfits: ['School uniform', 'Sky-blue hoodie', 'Cherry dress', 'Sporty red', 'Forest green'],
    outfitOn: (name: string) => `You’re wearing: ${name}`,
    restaurant: {
      title: '🍜 Restaurant menu',
      hint: 'Sit down for a proper meal (+energy, +mood).',
      dishes: [
        { name: 'Beef noodle soup', desc: '+35 energy' },
        { name: 'Dumplings (10)', desc: '+30 energy, +5 mood' },
        { name: 'Vegetable fried rice', desc: '+28 energy, +3 study (brain food!)' },
      ],
      eaten: (d: string) => `Yum! You enjoyed the ${d}.`,
      full: 'You’re too full to eat another meal today.',
    },
    nurse: {
      text: 'The office smells of clean sheets and lavender.',
      checkup: 'Get a check-up (+energy)',
      checkupDone: 'Nurse Hsieh checks your temperature. “All good! Rest a little and drink water.”',
      bandage: 'Ask about first aid',
      bandageText: 'For a small cut: wash it with clean water, press gently to stop bleeding, then cover it with a bandage. Tell an adult if it’s deep!',
      already: 'You already had a check-up today. Take care!',
    },
    pool: {
      text: 'The water sparkles under the skylight. The lifeguard is watching.',
      swim: 'Swim some laps (−energy, +mood)',
      swimDone: 'Splash! You swam ten laps. You feel strong and refreshed!',
      tired: 'You’re too tired to swim safely. Rest first!',
      rules: 'Pool rules',
      rulesText: '1. Shower before swimming. 2. No running on the deck. 3. Swim with a buddy. 4. Listen to the lifeguard.',
    },
    rally: {
      tennis: 'Tennis rally',
      volley: 'Volleyball practice',
      text: 'The ball is coming! Where will it land?',
      left: 'Left', center: 'Centre', right: 'Right',
      hit: 'Great return!',
      miss: 'Missed it! Watch the ball…',
      done: (n: number) => `Rally over: ${n}/3 good shots!`,
    },
    fountain: {
      text: 'Coins glitter at the bottom of the fountain.',
      wish: 'Toss a coin and make a wish (1🪙)',
      wishDone: 'Plink! You make a wish for your family and friends.',
      noCoin: 'You don’t have any coins.',
    },
    horse: {
      text: 'A gentle horse nuzzles your hand.',
      ride: 'Ride the horse (helmet on!)',
      dismount: 'Get off the horse',
      riding: 'You’re riding! Explore the paddock.',
      leave: 'The horse stops at the fence. Rides stay inside the paddock.',
      feed: 'Feed it a carrot (+mood)',
      fed: 'Crunch crunch! The horse looks very happy.',
    },
    scenarios: {
      swim: 'Ask about the pool rules', tennis: 'Learn to serve in tennis', horse: 'Ride a horse safely',
      clothes: 'Shop for clothes', music: 'Join music class', art: 'Talk about your painting',
    },
    gifts: {
      give: '🎁 Give a gift',
      pick: 'Which gift?',
      thanks: ['Oh! For me? Thank you so much!', 'You’re so thoughtful! I love it!', 'What a lovely surprise!'],
    },
  },
  /** Small talk for the new characters (same shape as `talks`). */
  talks2: {
    nurse: {
      greet: { morning: ['Good morning! Feeling well?'], afternoon: ['Hello, dear. Need anything?'], evening: ['Still here? Don’t forget to rest.'] },
      topics: [
        {
          say: 'Do you know why sleep is so important?',
          choices: [
            { t: 'It helps my body grow and heal.', reply: 'Exactly! And your brain sorts out everything you learned.' },
            { t: 'How many hours should I sleep?', reply: 'About nine or ten hours at your age.' },
            { t: 'Can I sleep in class instead?', reply: 'Haha! Sleep at night, learn in class.' },
          ],
        },
        {
          say: 'Washing your hands is the easiest way to stay healthy.',
          choices: [
            { t: 'I’ll wash them before eating!', reply: 'Great habit. Twenty seconds with soap!' },
            { t: 'Why twenty seconds?', reply: 'That’s how long it takes soap to break up the germs.' },
            { t: 'I’ll sing a song while washing.', reply: 'Perfect — “Happy birthday” twice is about twenty seconds!' },
          ],
        },
      ],
    },
    musicTeacher: {
      greet: { morning: ['Morning! Let’s make music.'], afternoon: ['Ah, a musician arrives!'], evening: ['One last song before home?'] },
      topics: [
        {
          say: 'Music is like a language. Everyone can learn it.',
          choices: [
            { t: 'I want to learn the piano!', reply: 'Wonderful. Start with one hand, slowly.' },
            { t: 'What instrument did you learn first?', reply: 'The recorder! Squeaky, but I loved it.' },
            { t: 'Can I learn the drums? Loudly?', reply: 'Haha, yes. Rhythm first, then volume.' },
          ],
        },
        {
          say: 'Do you sing when you’re happy?',
          choices: [
            { t: 'Yes! It makes me feel even better.', reply: 'Music doubles happiness, they say.' },
            { t: 'Can singing help when I’m sad?', reply: 'Often, yes. Gentle songs can calm your heart.' },
            { t: 'Only in the shower.', reply: 'The shower has the best acoustics!' },
          ],
        },
      ],
    },
    lifeguard: {
      greet: { morning: ['Morning swim? Warm up first!'], afternoon: ['Water’s perfect today.'], evening: ['Last laps, then home!'] },
      topics: [
        {
          say: 'Never swim alone, even if you’re a strong swimmer.',
          choices: [
            { t: 'Got it. Always with a buddy!', reply: 'That’s the rule. Safety first.' },
            { t: 'What if someone needs help?', reply: 'Shout for an adult or a lifeguard and throw something that floats. Don’t jump in.' },
            { t: 'Can my rubber duck be my buddy?', reply: 'Ha! Only a human buddy counts.' },
          ],
        },
      ],
    },
    barista: {
      greet: { morning: ['Good morning! Fresh croissants!'], afternoon: ['Welcome to Little Lantern!'], evening: ['Evening! Decaf for the grown-ups, cocoa for you.'] },
      topics: [
        {
          say: 'Kai from the corner store helped me open this café!',
          choices: [
            { t: 'That’s wonderful! It’s so cozy here.', reply: 'Thank you! We put a plant on every table.' },
            { t: 'What’s your favorite drink?', reply: 'Hot cocoa with a little cinnamon.' },
            { t: 'Is there a secret menu?', reply: 'Psst… ask for the “Lantern Special”. It has marshmallows.' },
          ],
        },
      ],
    },
    giftClerk: {
      greet: { morning: ['Hello! Looking for a present?'], afternoon: ['Welcome! Everything is wrapped for free.'], evening: ['Last-minute gift? I can help!'] },
      topics: [
        {
          say: 'The best gifts show you were really thinking about someone.',
          choices: [
            { t: 'I’ll choose something they really like.', reply: 'That’s the secret. Listen to what people love.' },
            { t: 'What do people buy the most?', reply: 'Flowers for moms and keychains for friends.' },
            { t: 'Can I gift myself?', reply: 'Haha, sometimes! Treat yourself.' },
          ],
        },
      ],
    },
    deptClerk: {
      greet: { morning: ['Good morning. New season, new styles!'], afternoon: ['Welcome. Try anything on!'], evening: ['We close at nine.'] },
      topics: [
        {
          say: 'Wear what makes you feel comfortable and confident.',
          choices: [
            { t: 'I like bright colours!', reply: 'Then the cherry dress or the red sports set!' },
            { t: 'What’s popular this year?', reply: 'Hoodies. Soft, warm, and they have pockets.' },
            { t: 'Do you have a superhero cape?', reply: 'Sadly not. Yet.' },
          ],
        },
      ],
    },
    waiter: {
      greet: { morning: ['We open at eleven, but come in!'], afternoon: ['Table for one?'], evening: ['Good evening! Busy night tonight.'] },
      topics: [
        {
          say: 'Our beef noodles take six hours to make the soup.',
          choices: [
            { t: 'Wow, no wonder it’s delicious!', reply: 'Thank you! The chef will be proud.' },
            { t: 'What else is good here?', reply: 'The dumplings. Folded by hand every morning.' },
            { t: 'Can I have dessert first?', reply: 'Only if you promise to eat the vegetables later!' },
          ],
        },
      ],
    },
    stableKeeper: {
      greet: { morning: ['Morning! The horses are hungry.'], afternoon: ['Want to say hi to the horses?'], evening: ['Time for the horses to rest.'] },
      topics: [
        {
          say: 'Always approach a horse from the side, so it can see you.',
          choices: [
            { t: 'I’ll be gentle and calm.', reply: 'Good. Horses can feel if you’re nervous.' },
            { t: 'What do horses like to eat?', reply: 'Hay, carrots and apples. Never chocolate!' },
            { t: 'Can horses talk?', reply: 'With their ears! Ears forward means curious.' },
          ],
        },
      ],
    },
  },
};
